import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { gql, useQuery, useMutation } from '@apollo/client';
import { setErrors } from 'Actions';
import { history } from 'Components/utils/router/history';
import LoadingSpinner from 'Utils/LoadingSpinner';
import ImportWizardHeader from './ImportWizardHeader';
import { followImportProgress } from 'HelperFunctions/import_wizard';
import { readRecent, saveRecent } from './RecentTranslations';

const API = import.meta.env.VITE_API_DOMAIN;

export const DREAM_IMPORT_SET = gql`
  query DreamImportSet($id: ID!) {
    dreamImportSet(id: $id) {
      id
      status
      message
      context
      costUsd
      dailyCapUsd
      files {
        id
        name
        size
        shape
        label
        system
        rows
        status
        message
        sourceShape
        mapping {
          source
          target
          transform
          confidence
          reasoning
        }
        droppedMapping {
          source
          target
          confidence
          reasoning
        }
        unmatchedColumns
      }
      ignored {
        name
        reason
      }
      outputs {
        id
        importType
        label
        rows
        fileName
        source
        status
        downloadUrl
      }
      usage {
        file
        model
        promptTokens
        completionTokens
        costUsd
      }
    }
  }
`;

const STAGE_OUTPUT = gql`
  mutation StageDreamImportOutput($input: StageDreamImportOutputInput!) {
    stageDreamImportOutput(input: $input) {
      importProgressId
      importType
      errors
    }
  }
`;

// The hand-off order (G): items first, then accessories and purchases, then bundles, then contacts.
const OUTPUT_ORDER = [
  'item',
  'accessory',
  'purchase',
  'bundle',
  'customer',
  'company',
  'venue',
];

// What the new tab shows while its rows are staged: it is a blank page with no app styles yet, so it is self-contained.
const preparingPage = (
  label
) => `<!doctype html><html><head><meta charset="utf-8">
<title>Preparing ${label}...</title><style>
body{margin:0;height:100vh;display:grid;place-items:center;background:#0f2a43;font-family:Roboto,Arial,sans-serif;color:#fff}
.box{position:relative;width:min(420px,86vw);padding:40px 32px;border-radius:16px;background:#163c5b;text-align:center;overflow:hidden;box-shadow:0 0 60px rgba(123,92,255,.45)}
.box::before{content:'';position:absolute;inset:-60%;background:conic-gradient(#278ada,#7b5cff,#d3347f,#ffb84d,#2c9a5b,#278ada);animation:spin 3s linear infinite;z-index:0}
.box::after{content:'';position:absolute;inset:3px;border-radius:13px;background:#163c5b;z-index:0}
.box>*{position:relative;z-index:1}
h1{font-size:20px;margin:0 0 6px}p{margin:0 0 24px;color:#b8c7d6;font-size:14px}
.track{height:6px;border-radius:3px;background:#0f2a43;overflow:hidden}
.swipe{height:100%;width:40%;border-radius:3px;background:linear-gradient(90deg,transparent,#278ada,#7b5cff,#d3347f);animation:swipe 1.4s ease-in-out infinite}
.arrow{font-size:28px;margin-bottom:12px;background:linear-gradient(90deg,#278ada,#7b5cff,#d3347f);-webkit-background-clip:text;background-clip:text;color:transparent;animation:nudge 1.4s ease-in-out infinite}
@keyframes spin{to{transform:rotate(1turn)}}
@keyframes swipe{from{transform:translateX(-100%)}to{transform:translateX(250%)}}
@keyframes nudge{50%{transform:translateX(8px)}}
@media (prefers-reduced-motion:reduce){*{animation:none!important}}
</style></head><body><div class="box"><div class="arrow">&#10140;</div>
<h1>Preparing your ${label.toLowerCase()} import</h1><p>Loading the translated rows into the import wizard...</p>
<div class="track"><div class="swipe"></div></div></div></body></html>`;

const percent = (value) => `${Math.round((value || 0) * 100)}%`;

// Columns of a file that were left out: unsure matches plus headers that match nothing.
const leftOut = (file) =>
  (file?.droppedMapping?.length || 0) + (file?.unmatchedColumns?.length || 0);
const plural = (count, word) =>
  `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`;

// Translate step (TD1-10584 F/G): one card per produced CSV first (ready, in progress or imported), each opening the
// unchanged Adjust step in a new tab so several imports run side by side. The per-file detection and how every column
// was matched (green = 90%+, red = left out: rename the header and drop the file again) fold under Translation details.
const Translate = () => {
  const { setId } = useParams();
  const dispatch = useDispatch();
  const business = useSelector((state) => state.business.business);
  const [staging, setStaging] = useState({});
  const { data, loading, error, refetch } = useQuery(DREAM_IMPORT_SET, {
    variables: { id: setId },
    fetchPolicy: 'network-only',
  });
  const [stageOutput] = useMutation(STAGE_OUTPUT);
  const set = data?.dreamImportSet;

  useEffect(() => {
    const permissions = business?.permissions;
    if (
      permissions &&
      !(permissions.importWizardEnabled && permissions.dreamImportWizardEnabled)
    ) {
      history.push('/unauthorized');
    }
  }, [business]);

  useEffect(() => {
    if (!set) return;
    if (set.status === 'incomplete' || set.status === 'uploading') {
      followImportProgress(set.id).then(
        () => refetch(),
        () => refetch()
      );
    }
  }, [set?.id, set?.status]);

  // A drop that is only TapGoods templates needs no translation: it goes straight to Adjust in this tab (AC3).
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!set || autoOpened.current || set.status !== 'completed') return;
    const passThrough =
      set.outputs.length === 1 &&
      set.files.length > 0 &&
      set.files.every((file) => file.shape === 'tapgoods');
    if (passThrough) {
      autoOpened.current = true;
      const output = set.outputs[0];
      // Its Start import button shows Preparing... meanwhile: a big file takes minutes to stage.
      setStaging((current) => ({ ...current, [output.id]: true }));
      stage(output)
        .then(
          (importType) => history.push(`/importWizard/adjust/${importType}`),
          (err) => fail(err, 'The file could not be staged.')
        )
        .finally(() =>
          setStaging((current) => ({ ...current, [output.id]: false }))
        );
    }
  }, [set?.id, set?.status]);

  // A translation opened from its link joins the remembered list too (kept at its place if already there).
  useEffect(() => {
    if (!set || !business) return;
    const known = readRecent(business.id).some(
      (entry) => String(entry.id) === String(set.id)
    );
    if (!known) {
      saveRecent(business.id, {
        id: set.id,
        at: new Date().toISOString(),
        files: set.files.map((file) => file.name),
      });
    }
  }, [set?.id, business?.id]);

  // Coming back from an import tab refreshes the cards, so a finished import shows as imported.
  useEffect(() => {
    const onFocus = () => refetch();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refetch]);

  const fail = (err, fallback) =>
    dispatch(setErrors(err?.response?.data?.error || err?.message || fallback));

  // The produced CSV's time-limited link comes with the set (downloadUrl); there is no REST download.
  const download = (output) => {
    if (!output.downloadUrl) {
      dispatch(setErrors('The file could not be downloaded.'));
      return;
    }
    const link = document.createElement('a');
    link.href = output.downloadUrl;
    link.download = output.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Stages the produced CSV into the wizard's staging table for its type; resolves with the type once done.
  const stage = (output) =>
    stageOutput({ variables: { input: { setId, outputId: output.id } } }).then(
      ({ data: result }) => {
        const payload = result?.stageDreamImportOutput;
        if (payload?.errors?.length) throw new Error(payload.errors.join(', '));
        return followImportProgress(payload.importProgressId).then(
          () => payload.importType
        );
      }
    );

  // The tab is opened on the click itself (a tab opened after an await is a blocked popup) and pointed at Adjust
  // once the rows are staged. Adjust reads its rows by type from the API, so each type works in its own tab.
  const openInNewTab = (output) => {
    if (staging[output.id] || output.status === 'imported') return;
    // Already waiting in Adjust: staging again would throw away the edits made there.
    if (output.status === 'open') {
      window.open(`/importWizard/adjust/${output.importType}`, '_blank');
      return;
    }
    const tab = window.open('', '_blank');
    if (tab) {
      tab.document.write(preparingPage(output.label));
      tab.document.close();
    }
    setStaging((current) => ({ ...current, [output.id]: true }));
    stage(output)
      .then((importType) => {
        const url = `/importWizard/adjust/${importType}`;
        // absolute: a blank popup has no base URL of its own to resolve a path against
        if (tab) tab.location.href = `${window.location.origin}${url}`;
        else history.push(url);
      })
      .catch((err) => {
        tab?.close();
        fail(err, 'The file could not be staged.');
      })
      .finally(() => {
        setStaging((current) => ({ ...current, [output.id]: false }));
        refetch();
      });
  };

  const outputs = [...(set?.outputs || [])].sort(
    (a, b) =>
      OUTPUT_ORDER.indexOf(a.importType) - OUTPUT_ORDER.indexOf(b.importType)
  );
  const running =
    set && (set.status === 'incomplete' || set.status === 'uploading');

  return (
    <div className='contentWrapper'>
      <ImportWizardHeader
        importStatus='translate'
        dream
        displayButtons={false}
        headerTitle='Your Translated Imports'
      />
      <div className='ImportWizardUploadContainer dreamImportTranslate'>
        {loading && !set && <LoadingSpinner />}
        {error && <p className='importWizardMessage warn'>{error.message}</p>}
        {set && (
          <>
            {running && (
              <p className='importWizardMessage'>
                Reading your files and matching every column to TapGoods...
              </p>
            )}
            {set.status === 'failed' && (
              <p className='importWizardMessage warn'>{set.message}</p>
            )}
            {!running && (
              <section className='dreamImportSection' id='dream_import_ready'>
                <h2 className='dreamImportReadyTitle'>
                  {outputs.length
                    ? `We found ${plural(outputs.length, 'import')} in your files`
                    : 'We found nothing to import in these files'}
                </h2>
                {outputs.length > 0 && (
                  <p className='hint'>
                    Import them in this order. Each one opens in its own tab, so
                    you can run several side by side.
                  </p>
                )}
                <div className='dreamImportCards'>
                  {outputs.map((output, index) => {
                    const source = set.files.find(
                      (file) => file.id === output.source
                    );
                    const missing = leftOut(source);
                    const imported = output.status === 'imported';
                    return (
                      <div
                        key={output.id}
                        id={`dream_import_card_${output.id}`}
                        className={`dreamImportCard ${output.status}`}
                      >
                        <span className='step'>
                          {imported ? '✓' : index + 1}
                        </span>
                        <strong>{output.label}</strong>
                        <span className='count'>
                          {plural(output.rows, 'row')}
                        </span>
                        <span className='from' title={source?.name}>
                          {source?.name || 'Combined reports'}
                        </span>
                        {imported ? (
                          <span className='chip done'>✓ Imported</span>
                        ) : missing ? (
                          <span className='chip partial'>
                            {plural(missing, 'column')} left out
                          </span>
                        ) : (
                          <span className='chip full'>
                            Every column matched
                          </span>
                        )}
                        {!imported && (
                          <button
                            type='button'
                            className='btn'
                            id={`dream_import_open_${output.importType}`}
                            disabled={staging[output.id]}
                            onClick={() => openInNewTab(output)}
                          >
                            {staging[output.id]
                              ? 'Preparing...'
                              : output.status === 'open'
                                ? 'Continue import'
                                : 'Start import'}
                          </button>
                        )}
                        <a
                          href='#'
                          className='download'
                          onClick={(event) => {
                            event.preventDefault();
                            download(output);
                          }}
                        >
                          Download CSV
                        </a>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            <details
              className='dreamImportSection dreamImportDetails'
              open={running}
            >
              <summary>
                Translation details
                {(() => {
                  const partial = set.files.filter(leftOut).length;
                  return partial ? (
                    <span className='chip partial'>
                      {plural(partial, 'file')} with columns left out
                    </span>
                  ) : (
                    !running && (
                      <span className='chip full'>Every file translated</span>
                    )
                  );
                })()}
              </summary>
              <table
                className={`grid dreamImportFilesTable ${running ? 'scanning' : ''}`}
              >
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Recognised as</th>
                    <th>Rows</th>
                    <th>Translation</th>
                  </tr>
                </thead>
                <tbody>
                  {set.files.map((file) => (
                    <tr key={file.id} id={`dream_import_file_${file.id}`}>
                      <td>{file.name}</td>
                      <td>
                        {file.label || 'Reading...'}
                        {file.system ? `, ${file.system}` : ''}
                      </td>
                      <td>
                        {file.rows == null ? '' : file.rows.toLocaleString()}
                      </td>
                      <td>
                        {file.status === 'empty' && (
                          <span className='chip muted'>Nothing to import</span>
                        )}
                        {file.status === 'blocked' && (
                          <span className='chip partial'>Not translated</span>
                        )}
                        {file.status === 'translated' &&
                          (leftOut(file) ? (
                            <span className='chip partial'>
                              {plural(leftOut(file), 'column')} left out
                            </span>
                          ) : (
                            <span className='chip full'>Fully translated</span>
                          ))}
                        {!['empty', 'blocked', 'translated'].includes(
                          file.status
                        ) && <span className='chip muted'>Reading...</span>}
                        {file.message && file.status !== 'empty' && (
                          <div className='hint'>{file.message}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {set.ignored.map((entry) => (
                    <tr key={entry.name} className='ignored'>
                      <td>{entry.name}</td>
                      <td colSpan={2}>{entry.reason}</td>
                      <td>
                        <span className='chip muted'>Skipped</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {set.files
                .filter(
                  (file) =>
                    file.mapping.length ||
                    file.droppedMapping.length ||
                    file.unmatchedColumns?.length
                )
                .map((file) => (
                  <div
                    className='dreamImportMappingBlock'
                    key={`mapping-${file.id}`}
                    id={`dream_import_mapping_${file.id}`}
                  >
                    <h3>Columns of {file.name}</h3>
                    <table className='grid dreamImportMapping'>
                      <thead>
                        <tr>
                          <th>Your column</th>
                          <th>TapGoods column</th>
                          <th>Confidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {file.mapping.map((entry) => (
                          <tr key={entry.source} className='recognised'>
                            <td>{entry.source}</td>
                            <td>
                              <span className='mark'>✓</span> {entry.target}
                            </td>
                            <td>{percent(entry.confidence)}</td>
                          </tr>
                        ))}
                        {file.droppedMapping.map((entry) => (
                          <tr key={entry.source} className='leftOut'>
                            <td>{entry.source}</td>
                            <td>
                              <span className='mark'>✕</span> Left out. Might be
                              "{entry.target}": rename the header to "
                              {entry.target}" and drop the file again.
                            </td>
                            <td>{percent(entry.confidence)}</td>
                          </tr>
                        ))}
                        {(file.unmatchedColumns || []).map((source) => (
                          <tr key={source} className='leftOut'>
                            <td>{source}</td>
                            <td>
                              <span className='mark'>✕</span> Left out: no
                              TapGoods column matches. Rename the header to a
                              TapGoods column name to import it.
                            </td>
                            <td></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              {set.usage.length > 0 && (
                <p className='hint' id='dream_import_usage'>
                  AI mapping: {set.usage.length} call(s), $
                  {set.costUsd.toFixed(4)} of the ${set.dailyCapUsd} daily
                  budget.
                </p>
              )}
            </details>
          </>
        )}
      </div>
    </div>
  );
};

export default Translate;
